import type { ComponentMeta } from "@mijalowcode/types";
import { ComponentCategory, PropType } from "@mijalowcode/types";

export const RowMeta: ComponentMeta = {
  componentName: "Row",
  package: "@mijadesign/web",
  title: "行",
  description: "栅格布局行容器",
  category: ComponentCategory.LAYOUT,
  icon: "MenuOutlined",
  visibleInComponentPanel: true,
  canHaveChildren: true,
  acceptedChildren: ["Col"],
  props: [
    {
      name: "gutter",
      label: "间隔",
      type: PropType.NUMBER,
      defaultValue: 0,
      description: "栅格间隔,单位 px",
      group: "基础",
    },
    {
      name: "justify",
      label: "水平排列",
      type: PropType.ENUM,
      defaultValue: "start",
      options: [
        { label: "左对齐", value: "start" },
        { label: "居中", value: "center" },
        { label: "右对齐", value: "end" },
        { label: "两端对齐", value: "space-between" },
        { label: "等间距", value: "space-around" },
        { label: "均匀分布", value: "space-evenly" },
      ],
      group: "布局",
    },
    {
      name: "align",
      label: "垂直对齐",
      type: PropType.ENUM,
      defaultValue: "top",
      options: [
        { label: "顶部", value: "top" },
        { label: "居中", value: "middle" },
        { label: "底部", value: "bottom" },
        { label: "拉伸", value: "stretch" },
      ],
      group: "布局",
    },
    {
      name: "wrap",
      label: "换行",
      type: PropType.BOOLEAN,
      defaultValue: true,
      group: "布局",
    },
  ],
  defaultProps: {
    gutter: 16,
  },
  defaultStyle: {
    gap: 16,
  },
  preview: [
    {
      title: "默认间隔",
      props: {
        gutter: 16,
        children: "栅格行容器",
      },
    },
    {
      title: "居中排列",
      props: {
        gutter: 24,
        justify: "center",
        align: "middle",
        children: "居中排列内容",
      },
    },
  ],
  docLinks: [
    {
      title: "Row 文档",
      url: "https://https://5x-ant-design.antgroup.com//components/row-cn",
      source: "antd",
    },
  ],
  npm: {
    package: "@mijadesign/web",
    exportName: "Row",
  },
  snippet: {
    componentName: "Row",
    package: "@mijadesign/web",
    props: { gutter: 16 },
    children: [],
  },
};

export const ColMeta: ComponentMeta = {
  componentName: "Col",
  package: "@mijadesign/web",
  title: "列",
  description: "栅格布局列",
  category: ComponentCategory.LAYOUT,
  icon: "PauseOutlined",
  visibleInComponentPanel: true,
  canHaveChildren: true,
  props: [
    {
      name: "span",
      label: "栅格数",
      type: PropType.NUMBER,
      defaultValue: 12,
      min: 1,
      max: 24,
      description: "占据的栅格数,0-24",
      group: "基础",
    },
    {
      name: "offset",
      label: "偏移",
      type: PropType.NUMBER,
      defaultValue: 0,
      min: 0,
      max: 24,
      description: "左侧偏移栅格数",
      group: "布局",
    },
    {
      name: "push",
      label: "右移",
      type: PropType.NUMBER,
      min: 0,
      max: 24,
      description: "向右移动栅格数",
      group: "布局",
    },
    {
      name: "pull",
      label: "左移",
      type: PropType.NUMBER,
      min: 0,
      max: 24,
      description: "向左移动栅格数",
      group: "布局",
    },
    {
      name: "order",
      label: "排序",
      type: PropType.NUMBER,
      description: "栅格顺序",
      group: "布局",
    },
    {
      name: "flex",
      label: "Flex 布局",
      type: PropType.STRING,
      description: '如 "1" 或 "100px"',
      group: "布局",
    },
  ],
  defaultProps: {
    span: 12,
  },
  preview: [
    {
      title: "半宽列",
      props: {
        span: 12,
        children: "半宽列内容",
      },
    },
    {
      title: "三分之一列",
      props: {
        span: 8,
        children: "三分之一列内容",
      },
    },
  ],
  docLinks: [
    {
      title: "Col 文档",
      url: "https://https://5x-ant-design.antgroup.com//components/col-cn",
      source: "antd",
    },
  ],
  npm: {
    package: "@mijadesign/web",
    exportName: "Col",
  },
  snippet: {
    componentName: "Col",
    package: "@mijadesign/web",
    props: { span: 12 },
    children: [],
  },
};

// Layout 布局容器
export const LayoutMeta: ComponentMeta = {
  componentName: "Layout",
  package: "antd",
  title: "布局容器",
  description: "页面布局容器",
  category: ComponentCategory.LAYOUT,
  icon: "AppstoreOutlined",
  canHaveChildren: true,
  acceptedChildren: ["Header", "Sider", "Content", "Footer", "Layout"],
  props: [
    {
      name: "hasSider",
      label: "包含侧边栏",
      type: PropType.BOOLEAN,
      defaultValue: false,
      description: "是否包含侧边栏",
      group: "基础",
    },
    {
      name: "style",
      label: "样式",
      type: PropType.OBJECT,
      group: "样式",
    },
  ],
  npm: {
    package: "antd",
    exportName: "Layout",
  },
  snippet: {
    componentName: "Layout",
    package: "antd",
    props: {},
    children: [],
  },
};

// Layout Header
export const HeaderMeta: ComponentMeta = {
  componentName: "Header",
  package: "antd",
  title: "头部",
  description: "页面头部区域",
  category: ComponentCategory.LAYOUT,
  icon: "BorderTopOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "style",
      label: "样式",
      type: PropType.OBJECT,
      group: "样式",
    },
    {
      name: "className",
      label: "类名",
      type: PropType.STRING,
      group: "样式",
    },
  ],
  npm: {
    package: "antd",
    exportName: "Layout.Header",
  },
  snippet: {
    componentName: "Header",
    package: "antd",
    props: {},
    children: [],
  },
};

// Layout Sider
export const SiderMeta: ComponentMeta = {
  componentName: "Sider",
  package: "antd",
  title: "侧边栏",
  description: "页面侧边栏",
  category: ComponentCategory.LAYOUT,
  icon: "MenuFoldOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "width",
      label: "宽度",
      type: PropType.NUMBER,
      defaultValue: 200,
      group: "布局",
    },
    {
      name: "collapsedWidth",
      label: "收起宽度",
      type: PropType.NUMBER,
      defaultValue: 80,
      group: "布局",
    },
    {
      name: "collapsed",
      label: "收起状态",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "状态",
    },
    {
      name: "collapsible",
      label: "可收起",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "交互",
    },
    {
      name: "style",
      label: "样式",
      type: PropType.OBJECT,
      group: "样式",
    },
  ],
  npm: {
    package: "antd",
    exportName: "Layout.Sider",
  },
  snippet: {
    componentName: "Sider",
    package: "antd",
    props: {},
    children: [],
  },
};

// Layout Content
export const ContentMeta: ComponentMeta = {
  componentName: "Content",
  package: "antd",
  title: "内容区",
  description: "页面主要内容区域",
  category: ComponentCategory.LAYOUT,
  icon: "BlockOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "style",
      label: "样式",
      type: PropType.OBJECT,
      group: "样式",
    },
    {
      name: "className",
      label: "类名",
      type: PropType.STRING,
      group: "样式",
    },
  ],
  npm: {
    package: "antd",
    exportName: "Layout.Content",
  },
  snippet: {
    componentName: "Content",
    package: "antd",
    props: {},
    children: [],
  },
};

// Layout Footer
export const FooterMeta: ComponentMeta = {
  componentName: "Footer",
  package: "antd",
  title: "底部",
  description: "页面底部区域",
  category: ComponentCategory.LAYOUT,
  icon: "BorderBottomOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "style",
      label: "样式",
      type: PropType.OBJECT,
      group: "样式",
    },
    {
      name: "className",
      label: "类名",
      type: PropType.STRING,
      group: "样式",
    },
  ],
  npm: {
    package: "antd",
    exportName: "Layout.Footer",
  },
  snippet: {
    componentName: "Footer",
    package: "antd",
    props: {},
    children: [],
  },
};
